import React from "react";
import { Span } from "./utils";
import { makeStyles } from "@material-ui/core/styles";

type ColorCellInfo = {
  color: string;
  colorMix: number;
  index: number;
  first: boolean;
  last: boolean;
};

export type RenderCell<T> = {
  cell: T;
  colors: ColorCellInfo[];
  isEdge: boolean;
  isEmpty: boolean;
  key: number;
  left?: RenderCell<T>;
  right?: RenderCell<T>;
};

type Props<T> = Omit<React.BaseHTMLAttributes<any>, "children"> & {
  cells: T[];
  spans: Span[];
  onMouseDown?: (e: React.SyntheticEvent<any>) => void;
  children: (
    cell: RenderCell<T>,
    maxColors: number,
    index: number
  ) => React.ReactElement | React.ReactNode;
};

export function Grid<T>({ onMouseDown, cells, spans, children, ...rest }: Props<T>) {
  const classes = useStyles({});

  const renderCells: Array<RenderCell<T>> = cells.map((cell, i) => {
    const colors = spans
      .filter((s) => i >= s.start && i <= s.end)
      .uniqueBy((x) => x.color)
      .map((s) => ({
        color: s.color,
        colorMix: s.colorMix,
        index: null,
        first: null,
        last: null,
        data: s.data?.toString() // Make data as you want before making it to string
      }));

    const isEdge = spans.any((s) => s.start === i || s.end === i);
    return {
      isEdge,
      isEmpty: !colors.any(),
      colors,
      key: i,
      cell
    };
  });

  renderCells.forEach((element, i) => {
    const left = renderCells[i - 1];
    const right = renderCells[i + 1];
    if (left) {
      left.right = element;
    }
    element.left = left;
    if (right) {
      right.left = element;
    }
    element.right = right;
    element.colors.forEach((x) => {
      const colorsLeft = (left && left.colors) || [];
      const colorsRight = (right && right.colors) || [];
      const colorLeft = colorsLeft.find((c) => c.color === x.color);
      const colorRight = colorsRight.find((c) => c.color === x.color);
      x.last = !colorRight;
      if (colorLeft) {
        x.index = colorLeft.index;
        x.first = false;
        return;
      }
      const indexes = colorsLeft
        .filter((c) => element.colors.any((y) => y.color === c.color))
        .map((c) => c.index)
        .concat(element.colors.map((y) => y.index));
      const maxIndex = indexes.max((c) => c) || 0;
      const possibleIndexes = [...new Array(maxIndex + 2)]
        .map((_, c) => c)
        .filter((c) => !indexes.includes(c));
      x.index = Math.min(...possibleIndexes);
      x.first = true;
    });
  });

  const maxColors = renderCells.map((x) => x.colors.length).max((x) => x);

  return (
    <div
      className={classes.root}
      onMouseDown={onMouseDown}
      data-spangridroot={true}
      {...rest}
    >
      {renderCells.map((c, i) => children(c, maxColors, i))}
    </div>
  );
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    userSelect: "none",
    alignItems: "center"
  }
});
